/**
 * DocumentDetail — `/documents/:id`
 *
 * Preview:
 *   - PDF     → `@react-pdf-viewer/core` with default-layout plugin
 *   - Image   → native `<img>` tag with object-fit contain
 *   - Other   → "no preview available" placeholder + download button
 *
 * Metadata sidebar shows: display_name, category, doc_type, file size,
 * mime, uploaded_at (relative + absolute tooltip), notes, tags,
 * effective_year.
 *
 * G3B will add a "Used in these applications" section below the
 * sidebar (deferred per your CP2 scope tightening).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileType, Loader2 } from "lucide-react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { api, formatApiError } from "@/api/client";
import DocumentsShell from "@/components/documents/DocumentsShell";
import UsedInApplications from "@/components/documents/UsedInApplications";
import { humanBytes, relativeTime } from "@/components/documents/DocumentCard";
import { humanDocType } from "@/components/documents/UploadWidget";

const PDF_WORKER_URL = "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js";

const CATEGORY_LABELS = {
  business_foundation: "Business Foundation",
  financial: "Financial",
  grant: "Grant",
  general: "General",
};

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, doc: null, error: null });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);

  const layoutPluginInstance = defaultLayoutPlugin();

  const loadDoc = useCallback(async () => {
    setState({ loading: true, doc: null, error: null });
    try {
      const res = await api.get(`/documents/${id}`);
      setState({ loading: false, doc: res.data, error: null });
    } catch (err) {
      setState({ loading: false, doc: null, error: formatApiError(err) });
    }
  }, [id]);

  useEffect(() => {
    loadDoc();
  }, [loadDoc]);

  // Once we have the doc, fetch the file as a blob so we can render
  // an in-browser preview without triggering a download.
  useEffect(() => {
    if (!state.doc) return;
    const mime = state.doc.mime_type || "";
    if (!mime.startsWith("image/") && mime !== "application/pdf") {
      return; // No preview for other MIMEs.
    }
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await api.get(`/documents/${id}/download`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const blob = new Blob([res.data], { type: mime });
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setPreviewErr(formatApiError(err));
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [state.doc, id]);

  async function handleDownload() {
    if (!state.doc) return;
    try {
      const res = await api.get(`/documents/${id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([res.data], { type: state.doc.mime_type }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = state.doc.display_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPreviewErr(formatApiError(err));
    }
  }

  return (
    <DocumentsShell testId="documents-detail-root">
      <div className="mt-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="documents-detail-back"
          className="inline-flex items-center gap-1 text-xs text-v2-textMuted hover:text-v2-accent transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to vault
        </button>
      </div>

      {state.loading && (
        <div
          data-testid="documents-detail-loading"
          className="mt-10 flex items-center gap-2 text-sm text-v2-textMuted"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading document…
        </div>
      )}

      {!state.loading && state.error && (
        <div
          data-testid="documents-detail-error"
          className="mt-10 rounded-3xl p-8"
          style={{
            background: "var(--v2-surface)",
            border: "1px solid var(--v2-border)",
          }}
        >
          <p className="text-sm text-v2-text">{state.error}</p>
          <Link
            to="/documents"
            data-testid="documents-detail-error-back"
            className="mt-3 inline-flex items-center gap-2 text-xs text-v2-accent hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Return to Documents
          </Link>
        </div>
      )}

      {!state.loading && !state.error && state.doc && (
        <>
          <header className="mt-4">
            <p className="text-[10px] uppercase tracking-[0.28em] text-v2-textMuted">
              {CATEGORY_LABELS[state.doc.category] || state.doc.category} ·{" "}
              {humanDocType(state.doc.doc_type)}
            </p>
            <h1
              data-testid="documents-detail-title"
              className="mt-2 text-[30px] sm:text-[36px] font-semibold tracking-tight text-v2-text"
            >
              {state.doc.display_name}
            </h1>
          </header>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <section className="lg:col-span-2">
              <div
                data-testid="documents-detail-preview"
                className="rounded-3xl overflow-hidden"
                style={{
                  background: "var(--v2-surface)",
                  border: "1px solid var(--v2-border)",
                  minHeight: 480,
                }}
              >
                {state.doc.mime_type === "application/pdf" && previewUrl && (
                  <div
                    data-testid="documents-detail-preview-pdf"
                    style={{ height: 620 }}
                  >
                    <Worker workerUrl={PDF_WORKER_URL}>
                      <Viewer
                        fileUrl={previewUrl}
                        plugins={[layoutPluginInstance]}
                      />
                    </Worker>
                  </div>
                )}
                {state.doc.mime_type?.startsWith("image/") && previewUrl && (
                  <img
                    src={previewUrl}
                    alt={state.doc.display_name}
                    data-testid="documents-detail-preview-image"
                    className="w-full h-auto object-contain"
                    style={{ maxHeight: 620 }}
                  />
                )}
                {(!state.doc.mime_type ||
                  (!state.doc.mime_type.startsWith("image/") &&
                    state.doc.mime_type !== "application/pdf")) && (
                  <div
                    data-testid="documents-detail-preview-unavailable"
                    className="p-10 text-center"
                  >
                    <FileType className="mx-auto w-8 h-8 text-v2-textMuted" />
                    <p className="mt-3 text-v2-text font-semibold">
                      No preview available for this file type.
                    </p>
                    <p className="mt-2 text-sm text-v2-textMuted">
                      Download the file to review its contents.
                    </p>
                    <button
                      type="button"
                      onClick={handleDownload}
                      data-testid="documents-detail-preview-download-fallback"
                      className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download {state.doc.display_name}
                    </button>
                  </div>
                )}
                {state.doc.mime_type &&
                  (state.doc.mime_type.startsWith("image/") ||
                    state.doc.mime_type === "application/pdf") &&
                  !previewUrl &&
                  !previewErr && (
                    <div className="p-10 flex items-center justify-center gap-2 text-sm text-v2-textMuted">
                      <Loader2 className="w-4 h-4 animate-spin" /> Preparing preview…
                    </div>
                  )}
                {previewErr && (
                  <div className="p-6 text-sm text-red-400">
                    Preview failed: {previewErr}
                  </div>
                )}
              </div>
            </section>

            <aside>
              <div
                className="rounded-3xl p-5"
                style={{
                  background: "var(--v2-surface)",
                  border: "1px solid var(--v2-border)",
                }}
              >
                <h2 className="text-xs uppercase tracking-[0.14em] text-v2-textMuted">
                  Details
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                      Category
                    </dt>
                    <dd className="text-v2-text">
                      {CATEGORY_LABELS[state.doc.category] || state.doc.category}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                      Document type
                    </dt>
                    <dd className="text-v2-text">
                      {humanDocType(state.doc.doc_type)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                      File size
                    </dt>
                    <dd className="text-v2-text">
                      {humanBytes(state.doc.file_size_bytes)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                      File type
                    </dt>
                    <dd className="text-v2-text text-xs">{state.doc.mime_type}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                      Uploaded
                    </dt>
                    <dd
                      className="text-v2-text"
                      title={new Date(state.doc.uploaded_at).toLocaleString()}
                    >
                      {relativeTime(state.doc.uploaded_at)}
                    </dd>
                  </div>
                  {state.doc.effective_year && (
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                        Effective year
                      </dt>
                      <dd className="text-v2-text">{state.doc.effective_year}</dd>
                    </div>
                  )}
                  {state.doc.tags && state.doc.tags.length > 0 && (
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                        Tags
                      </dt>
                      <dd className="mt-1 flex items-center gap-1 flex-wrap">
                        {state.doc.tags.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] text-v2-textMuted"
                            style={{
                              background: "rgba(148,163,184,0.10)",
                              border: "1px solid rgba(148,163,184,0.20)",
                            }}
                          >
                            #{t}
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                  {state.doc.notes && (
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
                        Notes
                      </dt>
                      <dd
                        data-testid="documents-detail-notes"
                        className="text-v2-text text-sm leading-relaxed whitespace-pre-wrap"
                      >
                        {state.doc.notes}
                      </dd>
                    </div>
                  )}
                </dl>
                <button
                  type="button"
                  onClick={handleDownload}
                  data-testid="documents-detail-download"
                  className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </button>
              </div>
            </aside>
          </div>

          {/* Phase G3B CP2 — "Used in these applications" reverse-index
              section. Deferred from G3A CP2 by explicit scope; now
              powered by /api/documents/{id}/used-in-applications. */}
          <UsedInApplications docId={state.doc.id} />
        </>
      )}
    </DocumentsShell>
  );
}
